import { inject, injectable } from 'inversify'
import { observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { ClockGateway } from '@core/services/clock.gateway'
import { LocalStorageGateway } from '@core/services/local-storage.gateway'
import type { GatewayFailure } from '@core/services/api-response.types'
import type { Schema } from '@core/validation/validation.schema'
import { UserModel } from '@core/infra/authentication/user.model'
import { APP_VERSION } from '@core/version'
import {
  CacheEnvelopeSchema,
  MAX_AGE_MS,
  MAX_ENTRY_BYTES,
  OFFLINE_CACHE_KEY_PREFIX,
  type OfflineCacheScope,
} from './offline-cache.config'

/**
 * Last-seen list data, persisted per user to `localStorage`, served to a
 * feature `load()` **only** when its request failed because the device is
 * offline — never over a real server answer (a 4xx/5xx means the server spoke,
 * and stale rows must not mask that).
 *
 * The mechanism is a pure pipe (CI-13): it stores and returns validated blobs
 * and decides nothing about *which* failures are eligible beyond the one
 * transport check `readOnNetworkFailure` makes, which is policy that belongs in
 * a Repository, not in a Gateway or a `core/services` helper.
 *
 * **Caveat, `identity` scope:** a user can hand-edit their own `localStorage`
 * to re-enable capability-gated UI affordances offline. This is harmless —
 * every route and every mutation is re-checked server-side, so the edit buys
 * nothing but a disabled button that does nothing when clicked — but it is an
 * authorization-shaped payload sitting in a user-writable store, and that is
 * recorded here rather than left to be discovered.
 */
@injectable()
export class OfflineCacheRepository implements UserScopedState, AppLifecycle {
  @inject(Types.ILocalStorageGateway)
  private storage!: LocalStorageGateway

  @inject(Types.IClockGateway)
  private clock!: ClockGateway

  @inject(UserModel)
  private userModel!: UserModel

  /**
   * scope → `cachedAtMs` of the entry currently being served from cache. Empty
   * when everything is live. Observable so ConnectivityPresenter's stale notice
   * recomputes the moment a scope starts (or stops) being served.
   */
  private served = observable.map<OfflineCacheScope, number>()

  /** Prune foreign-user, foreign-version and expired entries. Order-independent, safe to call twice. */
  initialize(): void {
    const now = this.clock.now().getTime()
    const currentEmail = this.userModel.email

    for (const key of this.storage.keys(OFFLINE_CACHE_KEY_PREFIX)) {
      const parsed = this.parseKey(key)
      const envelope = CacheEnvelopeSchema.safeParse(this.storage.getValue(key))

      const expired =
        !envelope.success || envelope.data.appVersion !== APP_VERSION || now - envelope.data.cachedAtMs > MAX_AGE_MS
      const foreign = parsed === null || (currentEmail !== '' && parsed.email !== currentEmail)

      if (expired || foreign) this.storage.remove(key)
    }
  }

  /** Fire-and-forget: a cache write must never fail a load. Skips when there is no user or the entry is too big. */
  write(scope: OfflineCacheScope, dtos: unknown): void {
    const email = this.userModel.email
    if (email === '') return

    const envelope = {
      v: 1 as const,
      appVersion: APP_VERSION,
      cachedAtMs: this.clock.now().getTime(),
      scope,
      payload: dtos,
    }
    if (JSON.stringify(envelope).length > MAX_ENTRY_BYTES) return

    const key = this.keyFor(email, scope)
    if (!this.storage.setValue(key, envelope)) {
      this.evictOldest(email)
      this.storage.setValue(key, envelope)
    }
    this.served.delete(scope)
  }

  /** Raw read — envelope + DTO re-parse, no eligibility check. Any doubt removes the key and returns null. */
  read<T>(scope: OfflineCacheScope, schema: Schema<T>): T | null {
    return this.readEnvelope(scope, schema)?.data ?? null
  }

  /**
   * The only method feature repositories call. Serves the cached DTOs when — and
   * only when — the load failed because the network transport threw.
   */
  readOnNetworkFailure<T>(scope: OfflineCacheScope, schema: Schema<T>, failure: GatewayFailure): T | null {
    if (failure.kind !== 'transport' || failure.reason !== 'network') return null

    const hit = this.readEnvelope(scope, schema)
    if (hit === null) return null

    this.served.set(scope, hit.cachedAtMs)
    return hit.data
  }

  wasServedFromCache(scope: OfflineCacheScope): boolean {
    return this.served.has(scope)
  }

  /** `cachedAtMs` of the entry currently served from cache for this scope, or null when it is live. */
  cachedAtMs(scope: OfflineCacheScope): number | null {
    return this.served.get(scope) ?? null
  }

  /** Every `cachedAtMs` currently being served — the presenter takes the oldest. */
  servedCachedAtMs(): readonly number[] {
    return [...this.served.values()]
  }

  clear(scope: OfflineCacheScope): void {
    const email = this.userModel.email
    if (email !== '') this.storage.remove(this.keyFor(email, scope))
    this.served.delete(scope)
  }

  resetUserScopedState(): void {
    // email is already blanked by clearSession() by the time this runs; previousEmail holds it.
    const email = this.userModel.email || this.userModel.previousEmail
    if (email !== '') {
      for (const key of this.storage.keys(this.keyFor(email, '' as OfflineCacheScope))) {
        this.storage.remove(key)
      }
    }
    this.served.clear()
  }

  private readEnvelope<T>(scope: OfflineCacheScope, schema: Schema<T>): { data: T; cachedAtMs: number } | null {
    const email = this.userModel.email
    if (email === '') return null

    const key = this.keyFor(email, scope)
    const envelope = CacheEnvelopeSchema.safeParse(this.storage.getValue(key))
    if (!envelope.success || envelope.data.appVersion !== APP_VERSION) {
      this.storage.remove(key)
      return null
    }

    const payload = schema.safeParse(envelope.data.payload)
    if (!payload.success) {
      this.storage.remove(key)
      return null
    }

    return { data: payload.data, cachedAtMs: envelope.data.cachedAtMs }
  }

  private keyFor(email: string, scope: OfflineCacheScope): string {
    return `${OFFLINE_CACHE_KEY_PREFIX}${email}:${scope}`
  }

  /** email is the middle segment; scope is the last one and never contains a colon. */
  private parseKey(key: string): { email: string; scope: string } | null {
    const rest = key.slice(OFFLINE_CACHE_KEY_PREFIX.length)
    const lastColon = rest.lastIndexOf(':')
    if (lastColon <= 0) return null
    return { email: rest.slice(0, lastColon), scope: rest.slice(lastColon + 1) }
  }

  private evictOldest(email: string): void {
    let oldestKey: string | null = null
    let oldestMs = Infinity
    for (const key of this.storage.keys(`${OFFLINE_CACHE_KEY_PREFIX}${email}:`)) {
      const envelope = CacheEnvelopeSchema.safeParse(this.storage.getValue(key))
      const at = envelope.success ? envelope.data.cachedAtMs : 0
      if (at < oldestMs) {
        oldestMs = at
        oldestKey = key
      }
    }
    if (oldestKey !== null) this.storage.remove(oldestKey)
  }
}
